import { children, derTlv, oidToString, readTlv, TAG } from '../bytes/der';
import { ParseError } from '../bytes/errors';
import { concat } from '../bytes/encoding';

/** The DER TBSCertificate (including its SEQUENCE header) of a certificate. */
export function extractTbs(certDer: Uint8Array): Uint8Array {
  const outer = readTlv(certDer, 0);
  if (outer.tag !== TAG.SEQUENCE) throw new ParseError('certificate: expected SEQUENCE');
  const [tbs] = children(outer);
  if (!tbs || tbs.tag !== TAG.SEQUENCE) throw new ParseError('certificate: expected TBSCertificate');
  return certDer.subarray(tbs.offset, tbs.end);
}

/** Elements of TBSCertificate by position (version is optional and explicitly tagged [0]). */
export interface TbsFields {
  version: number;
  serialNumber: Uint8Array;
  signatureAlgorithm: Uint8Array;
  issuer: Uint8Array;
  validity: Uint8Array;
  subject: Uint8Array;
  spki: Uint8Array;
  /** DER of the [3] EXPLICIT Extensions wrapper, if present */
  extensions?: Uint8Array;
}

export function tbsFields(tbs: Uint8Array): TbsFields {
  const seq = readTlv(tbs, 0);
  if (seq.tag !== TAG.SEQUENCE) throw new ParseError('TBSCertificate: expected SEQUENCE');
  const kids = [...children(seq)];
  let i = 0;
  let version = 1;
  if (kids[0]?.tag === 0xa0) {
    const v = readTlv(tbs, kids[0].start);
    version = (v.content[0] ?? 0) + 1;
    i = 1;
  }
  const slice = (k: (typeof kids)[number] | undefined, what: string): Uint8Array => {
    if (!k) throw new ParseError(`TBSCertificate: missing ${what}`);
    return tbs.subarray(k.offset, k.end);
  };
  const f: TbsFields = {
    version,
    serialNumber: slice(kids[i], 'serialNumber'),
    signatureAlgorithm: slice(kids[i + 1], 'signature'),
    issuer: slice(kids[i + 2], 'issuer'),
    validity: slice(kids[i + 3], 'validity'),
    subject: slice(kids[i + 4], 'subject'),
    spki: slice(kids[i + 5], 'subjectPublicKeyInfo'),
  };
  const ext = kids.slice(i + 6).find((k) => k.tag === 0xa3);
  if (ext) f.extensions = tbs.subarray(ext.offset, ext.end);
  return f;
}

/**
 * Wraps a bare TBSCertificate into a syntactically valid Certificate with an empty signature, so
 * X.509 parsers can display precertificate TBS data from log entries. Never use for verification.
 */
export function wrapTbsAsCertificate(tbs: Uint8Array): Uint8Array {
  const f = tbsFields(tbs);
  const emptySig = derTlv(TAG.BIT_STRING, new Uint8Array([0]));
  return derTlv(TAG.SEQUENCE, concat(tbs, f.signatureAlgorithm, emptySig));
}

/**
 * Returns the TBSCertificate with every extension of the given OID removed, re-encoding the
 * enclosing lengths (RFC 6962 §3.2: precert TBS = certificate TBS minus the SCT list / poison).
 * Returns the input unchanged when the extension is absent.
 */
export function removeExtension(tbs: Uint8Array, oid: string): Uint8Array {
  const seq = readTlv(tbs, 0);
  if (seq.tag !== TAG.SEQUENCE) throw new ParseError('TBSCertificate: expected SEQUENCE');
  const kids = [...children(seq)];
  const extIdx = kids.findIndex((k) => k.tag === 0xa3);
  if (extIdx === -1) return tbs;
  const wrapper = kids[extIdx]!;
  const list = readTlv(tbs, wrapper.start);
  if (list.tag !== TAG.SEQUENCE) throw new ParseError('Extensions: expected SEQUENCE');
  const kept: Uint8Array[] = [];
  let removed = 0;
  for (const ext of children(list)) {
    const [idTlv] = children(ext);
    if (idTlv && idTlv.tag === TAG.OID && oidToString(idTlv.content) === oid) {
      removed++;
      continue;
    }
    kept.push(tbs.subarray(ext.offset, ext.end));
  }
  if (removed === 0) return tbs;
  const parts: Uint8Array[] = [];
  for (const [i, k] of kids.entries()) {
    if (i !== extIdx) {
      parts.push(tbs.subarray(k.offset, k.end));
    } else if (kept.length > 0) {
      parts.push(derTlv(0xa3, derTlv(TAG.SEQUENCE, concat(...kept))));
    }
  }
  return derTlv(TAG.SEQUENCE, concat(...parts));
}
